import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, of, Subject } from 'rxjs';
import { finalize, map, mergeMap, takeUntil } from 'rxjs/operators';
import { IBuilding } from 'src/app/building-managment/building-management-interfaces';
import { Floor, IFloor, IUnit } from 'src/app/floor-management/floor-management-interface';
import { FloorManagementService } from 'src/app/floor-management/floor-management.service';
import { NotFoundError } from 'src/app/shared/errors/errors';
import { NotificationService } from 'src/app/shared/errors/notification.service';
import { getFullName } from 'src/app/shared/utils/full-name.util';
import { PopupProfile } from '../profile-management.service';
import { AssignFloor } from '../profile-assign-unit/profile-assign-unit.component';

@Component({
  selector: 'app-profile-assign-building',
  templateUrl: './profile-assign-building.component.html',
  styleUrls: ['./profile-assign-building.component.scss'],
})
export class ProfileAssignBuildingComponent implements OnDestroy {
  @Input() profileDetails!: PopupProfile;
  @Input() siteId!: string;
  @Input() buildings: IBuilding[] = [];
  @Input() dialogRef?: { close: () => void };
  @Input() stepper = 20;
  @Output() stepperChange = new EventEmitter<number>();
  @Input() selectedBuilding?: IBuilding;
  @Output() selectedBuildingChange = new EventEmitter<IBuilding>();
  @Output() floorsLoaded = new EventEmitter<AssignFloor[]>();
  isLoading = false;
  private destroyed = new Subject<void>();

  constructor(
    private floorService: FloorManagementService,
    private notificationService: NotificationService,
    private translate: TranslateService
  ) {}

  get userName(): string {
    const p = this.profileDetails;
    return getFullName(p?.firstName, p?.middleName, p?.lastName);
  }

  // Same sequence as the add-user building listing: floors, then units of every floor in parallel.
  selectBuilding(building: IBuilding): void {
    if (this.isLoading) return;
    this.isLoading = true;
    const { buildingId } = building;
    this.floorService.getAllFloorsByBuildingId(this.siteId, buildingId)
      .pipe(
        mergeMap((floors: Floor) => {
          const list: IFloor[] = floors || [];
          if (!list.length) return of([] as AssignFloor[]);
          return forkJoin(list.map(floor =>
            this.floorService.getUnitsAndAccessPointsByFloor(this.siteId, buildingId, floor.floorId)
          )).pipe(map(results => list.map((floor, i) => this.toAssignFloor(floor, results[i]))));
        }),
        takeUntil(this.destroyed),
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: floors => this.openUnits(building, floors),
        error: (error: any) => {
          if (error instanceof NotFoundError) this.openUnits(building, []);
          else this.notificationService.showError(error?.message);
        },
      });
  }

  private toAssignFloor(floor: IFloor, items: unknown): AssignFloor {
    const unitLabel = this.translate.instant('UNIT');
    const units = ((items as IUnit[]) || [])
      .filter(item => !!item?.unitId)
      .map(unit => ({
        unitId: unit.unitId,
        unitName: unit.unitName,
        label: `${unitLabel} ${unit.unitName}`,
        floorId: floor.floorId,
      }));
    return { floorId: floor.floorId, floorName: floor.floorName, wingName: floor.wingName, units };
  }

  private openUnits(building: IBuilding, floors: AssignFloor[]): void {
    this.selectedBuildingChange.emit((this.selectedBuilding = building));
    this.floorsLoaded.emit(floors);
    this.stepperChange.emit(21);
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  back(): void {
    this.stepperChange.emit(19);
  }

  close(): void {
    this.dialogRef?.close();
  }
}
